import { MainApi } from 'api/MainApi'
import type { FileData } from 'api/schema/models/FileData'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Loader from 'components/Loader/Loader'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FILES_LOADING_TYPE, PROGRESS_TYPES } from 'utils/constants'

import FilesList from './FilesList/FilesList'

import style from './SelectFileModal.module.css'

interface IDialogProps {
  isOpen: boolean
  mode: FILES_LOADING_TYPE
  facilityId?: number | null
  onConfirmClick: (name: string, id?: number) => void
  onClose: () => void
}

const SelectFileModal = ({
  isOpen,
  mode,
  facilityId,
  onConfirmClick,
  onClose,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [files, setFiles] = useState<any[]>([])
  const [selectedFile, setSelectedFile] = useState<any | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const allMapsCache = useRef<any[]>([])
  const PAGE_SIZE = 20

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = async (pageNum: number, search: string, isAppend = false) => {
    try {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.WORK)
      } else {
        setIsFetchingMore(true)
      }

      switch (mode) {
        case FILES_LOADING_TYPE.ROBOT_MAP: {
          const response = await MainApi.fetchRobotMaps(
            pageNum,
            PAGE_SIZE,
            search || undefined,
            facilityId ?? undefined
          )
          const mappedFiles = response.results.map(rm => ({
            name: rm.name || rm.map_id,
            id: rm.id,
            size: rm.file_size || 0,
            modified: rm.createdAt ? new Date(rm.createdAt).getTime() / 1000 : 0,
          }))

          if (isAppend) {
            setFiles(prev => [...prev, ...mappedFiles])
          } else {
            setFiles(mappedFiles)
          }

          setHasMore(pageNum * PAGE_SIZE < response.count)
          setPage(pageNum)
          break
        }
        case FILES_LOADING_TYPE.ROUTE: {
          const response = await MainApi.fetchRecordedRoutes(pageNum, PAGE_SIZE, search || undefined)
          const mappedFiles = response.results.map(route => ({
            name: route.properties.name || route.properties.filename || '',
            id: route.id,
            size: 0,
            modified: route.properties.recordedAt ? new Date(route.properties.recordedAt).getTime() / 1000 : 0,
          }))

          if (isAppend) {
            setFiles(prev => [...prev, ...mappedFiles])
          } else {
            setFiles(mappedFiles)
          }

          setHasMore(pageNum * PAGE_SIZE < response.count)
          setPage(pageNum)
          break
        }
        default:
          console.error('Invalid file loading mode:', mode)
          throw new Error('Invalid mode')
      }

      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      }
    } catch (error) {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.ERROR)
      }
      console.error('Error fetching files:', error)
    } finally {
      setIsFetchingMore(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchData(1, debouncedSearchQuery, false)
    } else {
      setSelectedFile(undefined)
      setSearchQuery('')
      setDebouncedSearchQuery('')
      setFiles([])
      allMapsCache.current = []
      setPage(1)
      setHasMore(true)
      setLoadingProgress(PROGRESS_TYPES.IDLE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearchQuery, mode])

  const loadMore = () => {
    if (loadingProgress === PROGRESS_TYPES.SUCCESS && hasMore && !isFetchingMore) {
      fetchData(page + 1, debouncedSearchQuery, true)
    }
  }

  const handleFileSelect = (file: FileData) => {
    setSelectedFile(file)
  }

  const handleConfirmClick = () => {
    if (selectedFile) {
      onConfirmClick(selectedFile.name, selectedFile.id)
      onClose()
    }
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      className={style.root}
      onClose={onClose}
      onConfirmClick={handleConfirmClick}
      title={mode === FILES_LOADING_TYPE.ROBOT_MAP ? 'Select a robot map' : 'Select a recorded route'}
      confirmText="Select"
      confirmButtonDisabled={!selectedFile}
    >
      <div className={style.content}>
        <input
          className={style.searchInput}
          type="text"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}
        {loadingProgress === PROGRESS_TYPES.SUCCESS && files.length > 0 ? (
          <FilesList files={files} onSelect={handleFileSelect} selectedFile={selectedFile} onScrollBottom={loadMore} />
        ) : loadingProgress === PROGRESS_TYPES.SUCCESS ? (
          <p>No items found</p>
        ) : null}
        {isFetchingMore && (
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <Loader />
          </div>
        )}
      </div>
    </ConfirmDialog>
  )
}

export default SelectFileModal
